import { useEffect } from 'react';
import { useProgress } from '@/state/progressStore';
import { normalizeCode } from './crypto';
import type { SyncEngine } from './syncEngine';

/** The pairing link's fragment: `https://…/#sync=ABCD…`. */
export const HASH_PREFIX = '#sync=';

/**
 * Wires the engine to the browser: pair from a scanned link, pull when the tab
 * comes back, and flush before it goes away.
 *
 * The fragment carries the secret and is never sent to the server, but it does
 * land in history and in the address bar, so it is consumed and stripped the
 * moment it is read — a shared screenshot or a shoulder-surfed URL bar is the
 * realistic leak here, not the network.
 */
export function useSyncLifecycle(engine: SyncEngine, onPaired?: (ok: boolean) => void): void {
  useEffect(() => {
    // Registered before anything can pull, or the first merge lands unheard.
    const stopAdopting = engine.onRemoteChange(() => {
      void useProgress.getState().adopt();
    });

    /**
     * Opening a pairing link while the app is already on screen is a
     * same-document navigation: nothing remounts, so this has to run on
     * `hashchange` as well as on mount or the tap would quietly do nothing.
     */
    const consumeHash = (): boolean => {
      if (!window.location.hash.startsWith(HASH_PREFIX)) return false;
      const code = normalizeCode(window.location.hash.slice(HASH_PREFIX.length));
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      if (!code) {
        onPaired?.(false);
        return true;
      }
      engine
        .pair(code)
        .then(() => onPaired?.(true))
        .catch(() => onPaired?.(false));
      return true;
    };

    // A device that is already paired catches up with whatever the others did.
    if (!consumeHash()) void engine.resume().catch(() => undefined);

    const onHashChange = (): void => void consumeHash();
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') void engine.flush().catch(() => undefined);
      else void engine.resume().catch(() => undefined);
    };
    const onOnline = (): void => void engine.resume().catch(() => undefined);

    window.addEventListener('hashchange', onHashChange);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    return () => {
      stopAdopting();
      window.removeEventListener('hashchange', onHashChange);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
    };
    // Once per mount: the engine is a singleton built at module scope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
